import { useRef } from 'react'

import { useHistory } from 'react-router-dom'

import { Types } from '@tribeplatform/gql-client/'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import {
  SystemSpaceSlug,
  useCreateCollection,
  useSpaceUtil,
  useUpdateCollection,
} from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Modal } from '@tribeplatform/react-ui-kit/Modal'

export const CollectionFormModal = ({
  collection,
  open,
  onClose,
}: {
  collection?: Types.Collection
  open: boolean
  onClose: () => void
}) => {
  const inputRef = useRef<HTMLInputElement>()
  const intl = useI18n()
  const { mutateAsync: createCollection, isLoading: isCreating } =
    useCreateCollection({
      fields: 'basic',
    })
  const { mutateAsync: updateCollection, isLoading: isUpdating } =
    useUpdateCollection()
  const history = useHistory()
  const { generatePath } = useSpaceUtil()
  const isNew = !collection?.id

  return (
    <Modal open={open} onClose={onClose} initialFocus={inputRef}>
      <Modal.Header
        title={
          isNew ? (
            <T
              defaultMessage="Create a new {COLLECTION}"
              id="Collection.Create"
            />
          ) : (
            <T defaultMessage="Edit {COLLECTION}" id="Collection.Edit" />
          )
        }
      />
      <Modal.Content>
        <Form
          defaultValues={collection}
          onSubmit={async data => {
            const { name, description } = data
            const input = {
              name,
              description,
            }

            if (collection?.id)
              return updateCollection({
                id: collection.id,
                input,
              }).then(() => {
                onClose()
              })

            return createCollection({
              input,
            }).then(collection => {
              onClose()
              history.push(
                generatePath(SystemSpaceSlug.COLLECTION, { id: collection.id }),
              )
            })
          }}
        >
          <div className="flex flex-col space-y-5">
            <Form.Input
              ref={inputRef}
              label={
                <T defaultMessage="Name" id="CollectionForm.Input.Name.Label" />
              }
              name="name"
              placeholder={intl.$t({
                defaultMessage: 'Choose a helpful name (e.g. Resources)',
                id: 'CollectionForm.Input.Name.Placeholder',
              })}
              validation={{
                required: intl.$t({
                  defaultMessage: 'Please enter a name for the {COLLECTION}',
                  id: 'CollectionForm.Input.Name.Validation.Required',
                }),
              }}
            />
            <Form.Textarea
              label={
                <T defaultMessage="Description" id="Generics.Description" />
              }
              name="description"
              placeholder={intl.$t({
                defaultMessage:
                  'What is this {COLLECTION} about? (e.g. All the resources you need to get started)',
                id: 'CollectionForm.Input.Description.Placeholder',
              })}
            />
            <Form.Actions>
              <Button
                variant="primary"
                type="submit"
                loading={isCreating || isUpdating}
              >
                {isNew ? (
                  <T defaultMessage="Create" id="Generics.Create" />
                ) : (
                  <T defaultMessage="Update" id="Generics.Update" />
                )}
              </Button>
              <Button variant="outline" onClick={onClose}>
                <T defaultMessage="Cancel" id="Generics.Cancel" />
              </Button>
            </Form.Actions>
          </div>
        </Form>
      </Modal.Content>
    </Modal>
  )
}
